/**
 * Download entries from the snapshot editor's shared asset library.
 *
 * The server already lists the library and serves every recognised asset over
 * a static URL. The browser therefore owns the rest: walk selected folders,
 * fetch the bytes, and either save one file directly or package the result in
 * the small dependency-free ZIP writer used by the snapshot Assets tab.
 */

import { getAssetStaticUri, listAssets } from "./snapshotEditorApi";
import { buildZipBytes } from "./zipArchive";

const DEFAULT_ARCHIVE_NAME = "asset-library-download.zip";

/** One library tile may represent one file, or both halves of a paired asset. */
export function filesForLibraryEntry(entry) {
  if (!entry || entry.type !== "file" || entry.selectable !== true) return [];

  const pair = [
    entry.imagePath && entry.imageUrl
      ? { path: entry.imagePath, url: entry.imageUrl }
      : null,
    entry.metaPath && entry.metaUrl
      ? { path: entry.metaPath, url: entry.metaUrl }
      : null,
  ].filter(Boolean);
  if (pair.length) return pair;

  return entry.relativePath && entry.url
    ? [{ path: entry.relativePath, url: entry.url }]
    : [];
}

/**
 * Expand selected files/folders into the recognised files behind them.
 * `listDirectory` is injectable so the recursive traversal stays unit-testable.
 */
export async function collectLibraryFiles(entries, listDirectory) {
  const files = new Map();
  let skipped = 0;

  async function visit(entry) {
    if (entry.type === "dir") {
      const children = await listDirectory(entry.relativePath);
      for (const child of children) await visit(child);
      return;
    }

    const expanded = filesForLibraryEntry(entry);
    if (!expanded.length) {
      skipped += 1;
      return;
    }
    expanded.forEach((file) => files.set(file.path, file));
  }

  for (const entry of entries || []) await visit(entry);
  return { files: [...files.values()], skipped };
}

function fileName(path) {
  return String(path || "asset").split("/").pop() || "asset";
}

function triggerDownload(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Firefox needs the object URL to outlive the click.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function readDirectory(baseID, dirPath) {
  const result = await listAssets(baseID, dirPath);
  if (!result.ok || result.data?.available === false) {
    const reason = result.ok ? result.data?.reason : result.error;
    throw new Error(`Could not read ${dirPath || "assets"} (${reason || "unknown error"})`);
  }
  return result.data?.entries || [];
}

/**
 * Download marked library entries. One real file keeps its original name;
 * folders, paired assets and multi-selection become one path-preserving ZIP.
 */
export async function downloadLibraryEntries(baseID, entries, onProgress) {
  const selected = entries || [];
  const { files, skipped } = await collectLibraryFiles(
    selected,
    (dirPath) => readDirectory(baseID, dirPath)
  );
  if (!files.length) return { count: 0, skipped };

  const downloaded = [];
  for (let index = 0; index < files.length; index += 1) {
    onProgress?.(`Downloading ${index + 1}/${files.length}…`);
    const file = files[index];
    const response = await fetch(getAssetStaticUri(file.url));
    if (!response.ok) throw new Error(`Could not read ${file.path} (${response.status})`);
    downloaded.push({ path: file.path, bytes: new Uint8Array(await response.arrayBuffer()) });
  }

  const direct = selected.length === 1 && selected[0].type === "file" && downloaded.length === 1;
  if (direct) {
    triggerDownload(new Blob([downloaded[0].bytes]), fileName(downloaded[0].path));
  } else {
    onProgress?.("Preparing archive…");
    triggerDownload(
      new Blob([buildZipBytes(downloaded)], { type: "application/zip" }),
      DEFAULT_ARCHIVE_NAME
    );
  }

  return { count: downloaded.length, skipped };
}
